import { Link, useLocation, matchPath } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useState, useEffect } from "react";

const setSharedNavigationState = (isOpen) => {
    window.dispatchEvent(new CustomEvent('club-navigation-toggle', {
        detail: { isOpen },
    }));
};

const Header = () => {
    const { user, isAuthenticated } = useAuth();
    const location = useLocation();
    const hideComps = matchPath('/scoreboard/:type/:id', location.pathname);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isAndroidTV, setIsAndroidTV] = useState(false);

    // Close mobile menu when route changes
    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [location.pathname]);

    // Close mobile menu when screen size changes to desktop
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 768) { // md breakpoint
                setSharedNavigationState(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const handleNavigationToggle = (event) => {
            setIsMobileMenuOpen(Boolean(event.detail?.isOpen));
        };

        window.addEventListener('club-navigation-toggle', handleNavigationToggle);
        return () => window.removeEventListener('club-navigation-toggle', handleNavigationToggle);
    }, []);

    useEffect(() => {
        const ua = navigator.userAgent.toLowerCase();
        const isAndroid = ua.indexOf("android") > -1;
        if (isAndroid) {
            setIsAndroidTV(true);
        }
    }, []);

    const navigationItems = [
        {
            to: "/",
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M5 12l-2 0l9 -9l9 9l-2 0" />
                    <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-7" />
                    <path d="M9 21v-6a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v6" />
                </svg>
            ),
            label: "Overview",
            compactLabel: "Overview",
            description: "Your club at a glance",
        },
        {
            to: "/club",
            activePrefixes: ["/club", "/table/stats/"],
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M12 5h3.5a5 5 0 0 1 0 10h-5.5l-4.015 4.227a2.3 2.3 0 0 1 -3.923 -2.035l1.634 -8.173a5 5 0 0 1 4.904 -4.019h3.4z" />
                    <path d="M14 15l4.07 4.284a2.3 2.3 0 0 0 3.925 -2.023l-1.6 -8.232" />
                    <path d="M8 9v2" />
                    <path d="M7 10h2" />
                    <path d="M14 10h2" />
                </svg>
            ),
            label: "Tables",
            compactLabel: "Tables",
            description: "Manage the room floor",
        },
        {
            to: "/games",
            activePrefixes: ["/games", "/game/"],
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M12 10m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
                    <path d="M12 14m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
                    <path d="M12 12m-8 0a8 8 0 1 0 16 0a8 8 0 1 0 -16 0" />
                </svg>
            ),
            label: "Matches",
            compactLabel: "Matches",
            description: "Live and scheduled play",
        },
        {
            to: "/players",
            activePrefixes: ["/players"],
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M12 13a3 3 0 1 0 0 -6a3 3 0 0 0 0 6z" />
                    <path d="M6.201 18.744a4 4 0 0 1 3.799 -2.744h4a4 4 0 0 1 3.798 2.741" />
                    <path d="M19.875 6.27c.7 .398 1.13 1.143 1.125 1.948v7.284c0 .809 -.443 1.555 -1.158 1.948l-6.75 4.27a2.269 2.269 0 0 1 -2.184 0l-6.75 -4.27a2.225 2.225 0 0 1 -1.158 -1.948v-7.285c0 -.809 .443 -1.554 1.158 -1.947l6.75 -3.98a2.33 2.33 0 0 1 2.25 0l6.75 3.98h-.033z" />
                </svg>
            ),
            label: "Players",
            compactLabel: "Players",
            description: "Club player directory",
        },
        {
            to: "/stats/snooker",
            activePrefixes: ["/stats/snooker"],
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M14 3v4a1 1 0 0 0 1 1h4" />
                    <path d="M17 21H7a2 2 0 0 1 -2 -2V5a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z" />
                    <path d="M9 12h6" />
                    <path d="M9 16h6" />
                </svg>
            ),
            label: "Performance",
            compactLabel: "Stats",
            description: "Breaks and match insights",
        },
        {
            to: `/queues/all/${user?.id}`,
            activePrefixes: ["/queues/", "/queue/"],
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M5 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0" />
                    <path d="M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    <path d="M21 21v-2a4 4 0 0 0 -3 -3.85" />
                </svg>
            ),
            label: "Queues",
            compactLabel: "Queues",
            description: "Who plays next",
        },
        {
            to: `/settings/${user?.id}`,
            activePrefixes: ["/settings"],
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M10.325 4.317c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756 .426 1.756 2.924 0 3.35a1.724 1.724 0 0 0 -1.066 2.573c.94 1.543 -.826 3.31 -2.37 2.37a1.724 1.724 0 0 0 -2.572 1.065c-.426 1.756 -2.924 1.756 -3.35 0a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065z" />
                    <path d="M9 12a3 3 0 1 0 6 0a3 3 0 0 0 -6 0" />
                </svg>
            ),
            label: "Settings",
            compactLabel: "Settings",
            description: "Pricing and preferences",
            utilityStart: true,
        },
        {
            to: "/tv-connect",
            activePrefixes: ["/tv-connect"],
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M3 7m0 2a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2z" />
                    <path d="M16 3l-4 4l-4 -4" />
                </svg>
            ),
            label: "TV Connect",
            compactLabel: "TV Connect",
            description: "Pair a club display",
        }
    ];

    const isNavigationItemActive = (item) => {
        if (item.to === "/") return location.pathname === "/";
        return (item.activePrefixes || [item.to]).some(prefix =>
            location.pathname === prefix || location.pathname.startsWith(prefix)
        );
    };

    const closeMobileNavigation = () => setSharedNavigationState(false);

    if (isAuthenticated() && !hideComps) {
        return (
            <nav className={`navbar-expand-md club-primary-navigation ${isAndroidTV ? 'android-tv-layout' : ''}`} aria-label="Club workspace">
                {isMobileMenuOpen && (
                    <button
                        type="button"
                        className="club-navigation-backdrop d-md-none"
                        aria-label="Close club navigation"
                        onClick={closeMobileNavigation}
                    />
                )}
                <div className={`collapse navbar-collapse club-navigation-collapse ${isMobileMenuOpen ? 'show' : ''}`} id="navbar-menu">
                    <div className="navbar club-navigation-bar">
                        <div className="container-xl club-navigation-inner">
                            {/* Desktop Navigation */}
                            <div className="club-navigation-pill d-none d-md-flex">
                                <span className="club-navigation-context d-none d-xl-inline-flex" aria-hidden="true">
                                    <i /> Club tools
                                </span>
                                <ul className="navbar-nav">
                                {navigationItems.map((item) => {
                                    const isActive = isNavigationItemActive(item);

                                    return (
                                        <li key={item.to} className={`nav-item club-nav-item ${item.utilityStart ? 'is-utility-start' : ''}`}>
                                            <Link
                                                className={`nav-link club-nav-link ${isActive ? 'active' : ''}`}
                                                to={item.to}
                                                aria-current={isActive ? 'page' : undefined}
                                            >
                                                <span className="nav-link-icon d-none d-lg-inline-grid" aria-hidden="true">
                                                    {item.icon}
                                                </span>
                                                <span className="nav-link-title club-nav-label-full">
                                                    {item.label}
                                                </span>
                                                <span className="nav-link-title club-nav-label-compact">{item.compactLabel}</span>
                                                <i className="club-nav-active-mark" aria-hidden="true" />
                                            </Link>
                                        </li>
                                    );
                                })}
                                </ul>
                            </div>

                            {/* Mobile Navigation */}
                            <div className="club-mobile-navigation d-md-none w-100">
                                <div className="club-mobile-navigation-heading">
                                <div>
                                    <small>CueClub workspace</small>
                                    <strong>Club tools</strong>
                                </div>
                            </div>
                                <div className="navbar-nav club-mobile-navigation-list">
                                    {navigationItems.map((item) => {
                                        const isActive = isNavigationItemActive(item);

                                        return (
                                            <div key={item.to} className="nav-item">
                                                <Link
                                                    className={`nav-link club-mobile-nav-link ${isActive ? 'active' : ''}`}
                                                    to={item.to}
                                                    onClick={closeMobileNavigation}
                                                    aria-current={isActive ? 'page' : undefined}
                                                >
                                                    <span className="nav-link-icon" aria-hidden="true">
                                                        {item.icon}
                                                    </span>
                                                    <span className="club-mobile-nav-copy">
                                                        <strong>{item.label}</strong>
                                                        <small>{item.description}</small>
                                                    </span>
                                                    <svg className="club-mobile-nav-arrow" viewBox="0 0 20 20" aria-hidden="true"><path d="M4 10h11m-4-4 4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                                </Link>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </nav>
        );
    }
};

export default Header;
